/**
 * The Publish checks panel (P6) — "is this ready?", answered continuously.
 *
 * Replaces PrePublishAuditTab. Three things change, and they are all about
 * frame rather than pixels:
 *
 *  1. Publish checks names readiness before publishing. The narrow rail uses
 *     Checks with the full name and an explainer on its trigger.
 *  2. The header is ONE SENTENCE in a live region, not a cluster of count
 *     chips. A chip cluster makes you do arithmetic; a sentence tells you.
 *  3. Rows come from ui/finding-list.js, which patches instead of rebuilding.
 *     The old renderer rewrote innerHTML on every report event — five times a
 *     second while you typed — and ate focus every time.
 *
 * Severity is a FILTER here, never the grouping. The grouping is the shared
 * remedy, because forty rows that all say "Image is missing alt text" were
 * never forty pieces of information.
 *
 * The internal tab id stays `audit`: it routes `props:request-tab`, the pane
 * element id and `panel:tab-changed`. Renaming the label without renaming the
 * id is the same move `client-seo` → "Project" already made.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { reportError } from '../core/report-error.js';
import { appendAuditFindings, FULL_AUDIT_CHECK_IDS } from '../shared/audit-engine.js';
import {
  createButton,
  setButtonBusy,
  isButtonBusy,
  createSegmentedControl,
  createCheckbox
} from './components.js';
import {
  createFindingList,
  readinessSentence,
  severityLabel,
  acceptUndoMessage
} from './finding-list.js';
import { disposeFindings } from '../shared/finding-disposition.js';
import { acceptFinding, isAcceptable } from '../core/audit-accepts.js';
import { showUndoToast } from './undo-toast.js';
import { bridge } from '../editing/bridge.js';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'blocking', label: severityLabel('blocking') },
  { id: 'warning', label: severityLabel('warning') },
  { id: 'cleanup', label: severityLabel('cleanup') },
  { id: 'accepted', label: 'Accepted (0)' }
];

const toFilter = (value) => (value === 'all' ? '' : value);
const fromFilter = (filter) => filter || 'all';

export class ShipPanel {
  constructor(container) {
    this._container = container;
    // Who is accepting. Read from the auth:state event rather than imported
    // from shell.js, which imports this module's host — a cycle.
    this._user = null;
    this._listeners = [];
    this._report = null;
    this._fullReport = null;
    this._exportFindings = [];
    this._exportCheckedAt = null;
    this._projectId = store.getState()?.projectId || null;
    this._projectGeneration = store.getEpoch() - store.getRevision();
    this._runId = null;
    this._active = false;
    this._showMaintenance = false;
    this._build();
    this._wire();
  }

  _build() {
    const root = document.createElement('div');
    root.className = 'pb-ship';

    // Header: the answer, then how fresh it is — one block, two registers.
    const head = document.createElement('div');
    head.className = 'pb-ship-head';
    // ── Readiness sentence. role=status + aria-live so a screen reader is told
    // when the answer changes; the pre-P6 surface announced nothing at all.
    this._headline = document.createElement('p');
    this._headline.className = 'pb-ship-headline';
    this._headline.setAttribute('role', 'status');
    this._headline.setAttribute('aria-live', 'polite');
    this._headline.textContent = 'Checking…';
    head.appendChild(this._headline);

    this._freshness = document.createElement('p');
    this._freshness.className = 'pb-ship-freshness';
    this._freshness.setAttribute('role', 'status');
    head.appendChild(this._freshness);
    root.appendChild(head);

    this._exportSummary = document.createElement('div');
    this._exportSummary.className = 'pb-ship-export-summary';
    this._exportSummary.hidden = true;
    root.appendChild(this._exportSummary);

    // ── Filter pills + maintenance toggle, one toolbar.
    const toolbar = document.createElement('div');
    toolbar.className = 'pb-ship-toolbar';
    this._filterControl = createSegmentedControl({
      options: FILTERS.map(({ id, label }) => ({ value: id, label })),
      ariaLabel: 'Filter by kind',
      value: 'all',
      className: 'pb-ship-filters pb-seg-compact',
      optionClassName: 'pb-ship-filter',
      labelClass: 'pb-btn-label',
      onChange: (value) => this._setFilter(toFilter(value))
    });
    this._filters = this._filterControl.el;
    toolbar.appendChild(this._filters);
    this._maintenance = createCheckbox({
      label: 'Show maintenance checks',
      checked: false,
      className: 'pb-ship-maintenance',
      onChange: (event) => {
        const checked = event.currentTarget.checked;
        this._showMaintenance = checked;
        this._list.setShowMaintenance(checked);
        this._render();
      }
    });
    toolbar.appendChild(this._maintenance.wrap);
    root.appendChild(toolbar);

    this._listHost = document.createElement('div');
    this._listHost.className = 'pb-ship-body';
    root.appendChild(this._listHost);

    this._list = createFindingList({
      findings: [],
      isAcceptable,
      onFilterChange: (filter) => this._filterControl.set(fromFilter(filter), { silent: true }),
      onAction: (finding) => eventBus.emit('pre-publish-audit:fix', finding),
      onAccept: (finding) => this._accept(finding),
      onClusterAction: (kind, rows) =>
        eventBus.emit('pre-publish-audit:cluster-fix', { kind, rows }),
      // Hover HIGHLIGHTS without scrolling, so the operator can survey the whole
      // list and see where each item lives before deciding what to touch.
      // Clicking is what scrolls.
      onHover: (finding) => this._highlight(finding),
      onNavigate: (finding) => eventBus.emit('pre-publish-audit:navigate', finding)
    });
    this._listHost.appendChild(this._list.el);

    const footer = document.createElement('div');
    footer.className = 'pb-ship-footer';
    this._recheck = createButton({
      variant: 'outline',
      size: 'sm',
      label: 'Re-check',
      onClick: () => this._requestFullAudit()
    });
    footer.appendChild(this._recheck);
    root.appendChild(footer);

    this._container.appendChild(root);
  }

  _wire() {
    this._listen('pre-publish-audit:report', (report) => {
      this._receiveReport(report);
    });
    this._listen('pre-publish-audit:run-started', ({ runId, projectId } = {}) => {
      if (projectId && projectId !== this._projectId) return;
      this._runId = runId;
      setButtonBusy(this._recheck, true, { busyLabel: 'Checking…' });
    });
    this._listen('pre-publish-audit:run-settled', ({ runId, projectId } = {}) => {
      if ((projectId && projectId !== this._projectId) || runId !== this._runId) return;
      this._runId = null;
      setButtonBusy(this._recheck, false);
    });
    this._listen('ship:export-findings', ({ findings = [], projectId } = {}) => {
      if (projectId && projectId !== this._projectId) return;
      this._exportFindings = appendAuditFindings(
        { findings: this._exportFindings },
        findings
      ).findings;
      this._exportCheckedAt = Date.now();
      this._report = appendAuditFindings(this._report || { projectId: this._projectId }, findings);
      this._render();
    });
    this._listen('ship:export-report', ({ report, summary, mode, stoppedKind = null } = {}) => {
      if (!report) return;
      this._receiveReport({
        ...report,
        tier: report.tier || report.auditTier || 'full'
      });
      this._renderExportSummary(report, summary, mode, stoppedKind);
      this._render();
    });
    this._listen('panel:tab-changed', ({ tab } = {}) => {
      const wasActive = this._active;
      this._active = tab === 'audit';
      if (this._active && !wasActive) this._requestFullAudit();
    });
    // An accept changes dispositions without changing findings, so the panel has
    // to re-derive on state change too.
    this._listen('auth:state', (user) => {
      this._user = user || null;
    });
    this._listen('state-changed', () => {
      const projectId = store.getState()?.projectId || null;
      const generation = store.getEpoch() - store.getRevision();
      if (projectId !== this._projectId || generation !== this._projectGeneration) {
        this._projectId = projectId;
        this._projectGeneration = generation;
        this._report = null;
        this._fullReport = null;
        this._exportFindings = [];
        this._exportCheckedAt = null;
        this._runId = null;
        setButtonBusy(this._recheck, false);
        this._exportSummary.hidden = true;
      }
      this._render();
    });
  }

  /**
   * What the last export actually did. A refused export used to read
   * "Exported snapshot" — the opposite of what happened — because the failure
   * path reuses this event with no summary.
   */
  _renderExportSummary(report, summary, mode, stoppedKind) {
    const el = this._exportSummary;
    el.classList.remove('pb-ship-export-summary--danger', 'pb-ship-export-summary--warning');
    if (stoppedKind) {
      el.textContent = `${stoppedKind} stopped — nothing ${
        stoppedKind === 'Publish' ? 'went live' : 'was downloaded'
      }.`;
      el.classList.add('pb-ship-export-summary--danger');
      el.hidden = false;
      return;
    }
    const anyway = (report?.findings || []).filter(
      (finding) => finding?.disposition === 'exportedAnyway'
    ).length;
    const pages = summary?.pages;
    const assets = summary?.assets;
    el.textContent = [
      mode === 'publish' ? 'Published' : 'Exported',
      Number.isFinite(pages) ? `${pages} page${pages === 1 ? '' : 's'}` : '',
      Number.isFinite(assets) ? `${assets} asset${assets === 1 ? '' : 's'}` : '',
      anyway ? `${anyway} problem${anyway === 1 ? '' : 's'} shipped anyway` : ''
    ]
      .filter(Boolean)
      .join(' · ');
    if (anyway) el.classList.add('pb-ship-export-summary--warning');
    el.hidden = false;
  }

  _requestFullAudit() {
    if (isButtonBusy(this._recheck)) return;
    setButtonBusy(this._recheck, true, { busyLabel: 'Checking…' });
    eventBus.emit('pre-publish-audit:run', { tier: 'full' });
  }

  _receiveReport(report) {
    if (!report || (report.projectId && report.projectId !== this._projectId)) return;
    const incoming = {
      ...report,
      projectId: report.projectId || this._projectId
    };
    const full = incoming.tiers?.includes('full') || incoming.tier === 'full';
    const narrower =
      full &&
      this._fullReport &&
      (!incoming.coverage ||
        incoming.coverage.pagesHydrated < this._fullReport.coverage?.pagesHydrated ||
        incoming.coverage.pagesTotal < this._fullReport.coverage?.pagesTotal);
    const previousFull = this._fullReport;
    if (full && !narrower) this._fullReport = incoming;
    const retained =
      previousFull && (!full || narrower)
        ? previousFull.findings.filter((finding) => FULL_AUDIT_CHECK_IDS.includes(finding.checkId))
        : [];
    this._report = appendAuditFindings(incoming, [...retained, ...this._exportFindings]);
    this._report.checkedAtByTier = {
      ...(incoming.checkedAtByTier || {}),
      cheap: incoming.checkedAtByTier?.cheap ?? incoming.checkedAt ?? incoming.timestamp ?? null,
      ...(this._fullReport
        ? {
            full:
              this._fullReport.checkedAtByTier?.full ??
              this._fullReport.checkedAt ??
              this._fullReport.timestamp ??
              null
          }
        : {})
    };
    this._render();
  }

  _renderFreshness() {
    if (!this._report) {
      this._freshness.textContent = 'Checks pending';
      return;
    }
    const full = this._fullReport;
    const coverage = this._report.coverage?.hydrationFailed?.length
      ? this._report.coverage
      : full?.coverage || this._report.coverage;
    const failed = coverage?.hydrationFailed || [];
    if (failed.length) {
      const names = failed.map(
        (id) => store.getState()?.pages?.find((page) => page.id === id)?.name || id
      );
      this._freshness.textContent = `Pages not checked: ${names.join(', ')}. Loading is incomplete.`;
      return;
    }
    const age = (time) => {
      if (!Number.isFinite(time)) return 'pending';
      const minutes = Math.max(0, Math.floor((Date.now() - time) / 60000));
      return minutes < 1 ? 'just now' : `${minutes} min ago`;
    };
    const parts = [`Page checks ${age(this._report.checkedAtByTier?.cheap)}`];
    if (full) {
      const assets = full.coverage?.assets;
      if (assets?.skipped) parts.push('Asset checks paused by Save-Data');
      else if (assets && assets.checked < assets.total)
        parts.push(`Asset checks incomplete: ${assets.checked} of ${assets.total} images checked`);
      else parts.push(`Asset checks from ${age(this._report.checkedAtByTier?.full)}`);
    } else parts.push('Asset checks pending');
    if (this._exportFindings.length)
      parts.push(`Export snapshot checks from ${age(this._exportCheckedAt)}`);
    this._freshness.textContent = parts.join(' · ');
  }

  _listen(event, fn) {
    eventBus.on(event, fn);
    this._listeners.push([event, fn]);
  }

  /** Current disposed view. Accepts come from state, findings from the report. */
  _disposed() {
    const findings = Array.isArray(this._report?.findings) ? this._report.findings : [];
    let accepts;
    try {
      accepts = store.getState()?.auditAccepts || null;
    } catch {
      // Expected-flow fallback: no store yet during boot.
      accepts = null;
    }
    return disposeFindings(findings, accepts);
  }

  _render() {
    this._renderFreshness();
    const disposed = this._disposed();
    const hiddenMaintenance = this._showMaintenance
      ? 0
      : disposed.open.filter(
          (finding) => finding.kind === 'maintenance' && finding.severity !== 'blocking'
        ).length;
    this._headline.textContent = readinessSentence(disposed.counts, { hiddenMaintenance });
    const acceptedCount = disposed.accepted.length;
    const accepted = this._filterControl.optionEl('accepted');
    const label = `Accepted (${acceptedCount})`;
    accepted.querySelector('.pb-btn-label').textContent = label;
    accepted.setAttribute('aria-label', label);
    this._filterControl.set(fromFilter(this._list.getFilter()), { silent: true });
    this._list.update(disposed.findings);
  }

  _setFilter(id) {
    this._list.setFilter(id);
    this._filterControl.set(fromFilter(id), { silent: true });
  }

  async _accept(finding) {
    const by = this._user && this._user.sub ? String(this._user.sub) : null;
    let ok;
    try {
      ok = await acceptFinding(finding, { by });
    } catch (err) {
      reportError('Accept finding', err, {
        user: 'toast',
        message: 'Could not accept that item. Try again.'
      });
      return;
    }
    if (!ok) return;
    // The undo IS the confirmation (picker-patterns rule) — never a success
    // toast beside it.
    this._list.removeFinding(finding.id);
    // Keep accepted findings in the list's source set after focus recovery.
    this._render();
    showUndoToast(acceptUndoMessage(finding), {
      id: `undo-accept-${finding.id}`,
      duration: 3000
    });
  }

  /** Outline the target in the canvas WITHOUT scrolling to it. */
  _highlight(finding) {
    const loc = finding?.location || {};
    if (!finding) {
      bridge.send('highlight-eid', { instanceId: null, eid: null });
      return;
    }
    if (!loc.instanceId && !loc.eid) return;
    bridge.send('highlight-eid', {
      instanceId: loc.instanceId || null,
      eid: loc.eid || null
    });
  }

  destroy() {
    for (const [event, fn] of this._listeners) eventBus.off(event, fn);
    this._listeners = [];
  }
}
